
import { Button } from "@/components/ui/button";
import { QuestionRenderer } from "./QuestionRenderer";
import { Loader2 } from "lucide-react";
import type { Database } from "@/integrations/supabase/types";

type SupportedLanguage = Database["public"]["Enums"]["supported_language"];
type ParticipantGroup = Database["public"]["Enums"]["participant_group"];
type Survey = Database["public"]["Tables"]["surveys"]["Row"];

interface SurveyQuestionsProps {
  survey: Survey;
  language: SupportedLanguage;
  answers: Record<string, string>;
  isSubmitting: boolean;
  participantGroup?: ParticipantGroup | null;
  previousAnswers?: Record<string, string> | null; // Add previousAnswers prop
  onAnswerChange: (questionId: string, value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
}

// RTL languages
const RTL_LANGUAGES = ["ar", "fa", "prs"];

export const SurveyQuestions = ({
  survey,
  language,
  answers,
  isSubmitting,
  participantGroup,
  previousAnswers, // Add previousAnswers
  onAnswerChange,
  onSubmit,
}: SurveyQuestionsProps) => {
  const isRtl = RTL_LANGUAGES.includes(language);
  
  // Check if this is a read-only view (previous answers exist and answers are the same)
  const isReadOnly = previousAnswers && Object.keys(previousAnswers).length > 0 && 
                     JSON.stringify(answers) === JSON.stringify(previousAnswers);
  
  // Handle the case where questions might be undefined or not in the correct format
  const questions = Array.isArray(survey.questions?.[language]) 
    ? (survey.questions?.[language] as Array<{
        id: string;
        type: string;
        question: string;
        required: boolean;
        options?: string[];
        showForGroups?: ParticipantGroup[];
      }>) 
    : [];
  
  console.log(`Survey questions for language ${language}:`, questions);
  
  const filteredQuestions = questions.filter(question => {
    if (!question.showForGroups || question.showForGroups.length === 0) {
      return true;
    }
    
    if (!participantGroup) {
      return true;
    }
    
    return question.showForGroups.includes(participantGroup);
  });

  const getSubmitButtonText = () => {
    const submitTextMap: Record<string, string> = {
      en: "Continue",
      sv: "Fortsätt",
      ar: "متابعة",
      ti: "ቀጽል",
      fa: "ادامه",
      prs: "ادامه",
      so: "Sii wad",
      uk: "Продовжити",
    };
    
    return submitTextMap[language] || "Continue";
  };
  
  const getRequiredText = () => {
    const requiredTextMap: Record<string, string> = {
      en: "* Required",
      sv: "* Obligatorisk",
      ar: "* مطلوب",
      ti: "* የግድ",
      fa: "* ضروری",
      prs: "* ضروری",
      so: "* Loo baahan yahay",
      uk: "* Обов'язково",
    };
    
    return requiredTextMap[language] || "* Required";
  };
  
  const getLoadingText = () => {
    const loadingTextMap: Record<string, string> = {
      en: "Submitting...",
      sv: "Skickar...",
      ar: "جار الإرسال...",
      ti: "ይላኣኽ ኣሎ...",
      fa: "در حال ارسال...",
      prs: "در حال ارسال...",
      so: "Waa la diraa...",
      uk: "Надсилання...",
    };
    
    return loadingTextMap[language] || "Submitting...";
  };
  
  const getAlreadySubmittedText = () => {
    const submittedTextMap: Record<string, string> = {
      en: "Response Already Submitted",
      sv: "Svar redan inskickat",
      ar: "تم إرسال الرد بالفعل",
      ti: "መልሲ ቀድሙ ተለኣኺዩ",
      fa: "پاسخ قبلاً ارسال شده",
      prs: "پاسخ قبلاً ارسال شده",
      so: "Jawaabta ayaa horay loo diray",
      uk: "Відповідь вже надіслано",
    };
    
    return submittedTextMap[language] || "Response Already Submitted";
  };
  
  const getViewOnlyText = () => {
    const viewTextMap: Record<string, string> = {
      en: "You can view your previous answers below, but cannot modify them.",
      sv: "Du kan se dina tidigare svar nedan, men kan inte ändra dem.",
      ar: "يمكنك عرض إجاباتك السابقة أدناه، ولكن لا يمكنك تعديلها.",
      ti: "ንድሕሪት መልስታትካ ኣብ ታሕቲ ክትሪኦ ትኽእል ኢኻ፣ ግን ክትቕይሮም ኣይትኽእልን።",
      fa: "می‌توانید پاسخ‌های قبلی خود را در زیر مشاهده کنید، اما نمی‌توانید آن‌ها را تغییر دهید.",
      prs: "می‌توانید پاسخ‌های قبلی خود را در زیر مشاهده کنید، اما نمی‌توانید آن‌ها را تغییر دهید.",
      so: "Waxaad arki kartaa jawaabadaadii hore ee hoose, laakiin ma beddeli kartid.",
      uk: "Ви можете переглянути свої попередні відповіді нижче, але не можете їх змінювати.",
    };
    
    return viewTextMap[language] || "You can view your previous answers below, but cannot modify them.";
  };

  // If there are no questions, handle it gracefully
  if (!filteredQuestions || filteredQuestions.length === 0) {
    return (
      <div className="text-center p-4">
        <p>No questions available for this survey in {language} language.</p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-8 w-full" dir={isRtl ? "rtl" : "ltr"}>
      {isReadOnly && (
        <div className={`bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 ${isRtl ? 'text-right' : 'text-left'}`}>
          <h3 className="font-semibold text-blue-900 mb-2">
            {getAlreadySubmittedText()}
          </h3>
          <p className="text-blue-700 text-sm">
            {getViewOnlyText()}
          </p>
        </div>
      )}
      
      {!isReadOnly && (
        <div className={`text-sm text-muted-foreground mb-4 w-full ${isRtl ? 'text-right' : 'text-left'}`}>
          {getRequiredText()}
        </div>
      )}
      
      <div className="space-y-6 w-full">
        {filteredQuestions.map((question) => (
          <QuestionRenderer
            key={question.id}
            question={question}
            value={answers[question.id] || ""}
            onChange={isReadOnly ? () => {} : (value) => onAnswerChange(question.id, value)} // Disable onChange if read-only
            language={language}
          />
        ))}
      </div>

      {!isReadOnly && (
        <Button
          type="submit"
          className={`w-full ${isRtl ? 'rtl:text-right' : ''}`}
          disabled={isSubmitting}
          dir={isRtl ? "rtl" : "ltr"}
        >
          {isSubmitting ? (
            <div className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>{getLoadingText()}</span>
            </div>
          ) : (
            getSubmitButtonText()
          )}
        </Button>
      )}
    </form>
  );
};
